import React, {
  useEffect,
  useCallback,
  useRef,
  useState,
  useMemo,
} from 'react';

import classNames from 'classnames';
import useAutocomplete, {
  createFilterOptions,
} from '@mui/material/useAutocomplete';
import { InputAdornment } from '@mui/material';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Popper from '@mui/material/Popper';
import FormControl from '@mui/material/FormControl';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ClickAwayListener from '@mui/material/ClickAwayListener';

import { Box } from '../../Box';
import { Icon } from '../../Icon';
import { Label, Preloader } from '../common';
import { Paper } from '../../Paper';
import { TreeViewItem as Item, TreeView } from '../../TreeView';
import { Typography } from '../../Typography';
import { CircularProgress } from '../../CircularProgress';

import { ListBox } from './ListBox';
import { useStyles } from './TreeViewAutocomplete.styles';

const MIN_INPUT_VALUE_LENGTH = 3;

type TreeNode<T> = {
  children?: Option<T>[];
};

interface Filter {
  limit: number;
  offset: number;
  query: string;
}

type Option<T> = Omit<T, 'children'> & TreeNode<T>;

interface TreeViewAutocompleteProps<T> {
  disabled?: boolean;
  name?: string;
  label?: string;
  tooltip?: React.ReactNode;
  required?: boolean;
  options: Option<T>[];
  noOptionsText?: string;
  placeholder?: string;
  clearable?: boolean;
  limit?: number;
  getOptionLabel?: (option: T) => string;
  value: string | undefined;
  onChange: (value?: string, item?: Item) => void;
  onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
  onSearch?: (filter: Filter) => Promise<T[]>;
  onChildrenLoad?: (unitId: string) => Promise<T[]>;
  onLoadTree?: (unitId: string) => Promise<Option<T>[]>;
  error?: boolean;
  helperText?: string;
  loading?: boolean;
  testId?: string;
  renderSearchOption?: (
    option: T,
    highlightedLabel: React.ReactNode,
  ) => React.ReactNode;
  renderOption?: (item: Item) => React.ReactNode;
  withoutMargins?: boolean;
}

/**
 * Компонент TreeViewAutocomplete представляет собой универсальное поле ввода, поддерживающее выбор данных в древовидной структуре.
 * Он объединяет функциональность автодополнения с древовидным представлением для вложенных опций, что делает его подходящим
 * для приложений, требующих иерархического выбора данных, таких как организационные диаграммы, структуры файловых директорий и т.д.
 *
 * @param {Object} props - Свойства компонента TreeViewAutocomplete.
 * @param {boolean} [props.disabled=false] - Если true, компонент отключен.
 * @param {string} [props.name] - Имя компонента.
 * @param {string} [props.label] - Подпись для поля ввода.
 * @param {React.ReactNode} [props.tooltip] - Необязательная подсказка для отображения с подписью.
 * @param {boolean} [props.required=false] - Если true, поле ввода отмечено как обязательное.
 * @param {Option<T>[]} props.options - Массив опций для отображения в древовидном представлении.
 * @param {string} [props.noOptionsText='Ничего не найдено'] - Текст для отображения, когда никакие опции не соответствуют введенному значению.
 * @param {string} [props.placeholder=''] - Текст заполнителя для поля ввода.
 * @param {boolean} [props.clearable=true] - Если true, компонент можно очистить.
 * @param {number} [props.limit=50] - Максимальное количество загружаемых опций за один раз.
 * @param {function} [props.getOptionLabel] - Функция для извлечения текста подписи из опции.
 * @param {string} props.value - Текущее значение компонента.
 * @param {function} props.onChange - Колбэк-функция для обработки изменений выбранного значения.
 * @param {function} props.onBlur - Колбэк-функция для обработки события потери фокуса полем ввода.
 * @param {function} [props.onSearch] - Асинхронная функция для загрузки опций на основе введенного значения.
 * @param {function} [props.onLoadTree] - Асинхронная функция для загрузки дочерних узлов дерева.
 * @param {function} [props.onChildrenLoad] - Асинхронная функция для загрузки дочерних узлов дерева, обычно используется для ленивой загрузки.
 * @param {string} [props.testId] - Тестовый идентификатор для поля ввода, полезен для целей тестирования.
 * @param {boolean} [props.error=false] - Если true, поле ввода отмечено как имеющее ошибку.
 * @param {string} [props.helperText] - Необязательный текст-помощь для отображения под полем ввода.
 * @param {string} [props.withoutMargins] - Необязательный флаг, для отключения внешних отступов.
 * @param {number} [MIN_INPUT_VALUE_LENGTH=1] - Минимальная длина введенного значения, необходимая для открытия древовидного представления или отображения результатов поиска.
 * @param {function} [props.renderSearchOption] - Кастомный метод рендера опции в списке поиска.
 * @param {function} [props.renderOption] - Кастомный метод рендера элементов в древовидном представлении.
 */

export const TreeViewAutocomplete = <
  T extends {
    id: string;
    label: string;
    hasChildrenToLoad?: boolean;
    disabled?: boolean;
  },
>({
  disabled = false,
  label,
  tooltip,
  options,
  placeholder = '',
  clearable = true,
  required = false,
  getOptionLabel = (option) => option.label || '',
  noOptionsText = 'Ничего не найдено',
  onChange,
  onBlur,
  onSearch,
  onChildrenLoad,
  onLoadTree,
  value,
  error = false,
  helperText = '',
  loading = false,
  testId = 'test-id-tree-view-autocomplete',
  limit = 50,
  withoutMargins = false,
  renderSearchOption,
  renderOption,
  ...rest
}: TreeViewAutocompleteProps<T>) => {
  const { classes } = useStyles();

  const [isLoading, setLoading] = useState(false);
  const [isPaginationLoading, setPaginationLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [unitTree, setUnitTree] = useState<Option<T>[]>(options);
  const [loadedOptions, setLoadedOptions] = useState<T[]>([]);
  const [open, setOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [focused, setFocused] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [cachedValue, setCachedValue] = useState('');
  const [filter, setFilter] = useState<Filter>({
    limit,
    offset: 0,
    query: '',
  });

  const isDisabled = disabled || loading;

  const treeOpen = !isDisabled && open;

  useEffect(() => {
    if (treeOpen && onLoadTree && value) {
      setLoading(true);
      onLoadTree(value)
        .then((result) => {
          setUnitTree(result);
          setLoading(false);
        })
        .catch(() => setLoading(false));
    }
  }, [onLoadTree, treeOpen, value]);

  const handleChange = useCallback(
    (nextValue?: string, item?: Item) => {
      onChange(nextValue, item);
      setOpen(false);
    },
    [onChange],
  );

  const handleClearClick = useCallback(
    (e: React.MouseEvent) => {
      if (!focused) {
        e.preventDefault();
        e.stopPropagation();
      }
      setInputValue('');
      setCachedValue('');
      onChange('');
    },
    [focused, onChange],
  );

  const getFlatTree = useCallback((array: Option<T>[]) => {
    let result: T[] = [];
    array.forEach((item) => {
      if (item.children instanceof Array) {
        const { children, ...itemRest } = item;
        result.push(itemRest as T);
        result = [...result, ...getFlatTree(children)];
      } else {
        result.push(item as T);
      }
    });
    return result;
  }, []);

  const flatOptions = useMemo(
    () => getFlatTree(options).sort((a, b) => (a.label > b.label ? 1 : -1)),
    [getFlatTree, options],
  );

  useEffect(() => {
    setUnitTree(options);
  }, [options]);

  useEffect(() => {
    if (value) {
      const foundOption = flatOptions.find((opt) => opt.id === value);
      if (foundOption) {
        setInputValue(foundOption.label);
        setCachedValue(foundOption.label);
      } else if (cachedValue) {
        setInputValue(cachedValue);
      } else {
        setInputValue(value);
        setCachedValue(value);
      }
    }
  }, [value, flatOptions, cachedValue]);

  const handleSearch = useCallback(
    (newValue: string) => {
      if (newValue && onSearch) {
        setLoading(true);
        setFilter({
          offset: 0,
          limit: 50,
          query: '',
        });
        setHasMore(true);
        onSearch({
          query: newValue,
          limit: 50,
          offset: 0,
        })
          .then((result) => {
            setLoadedOptions(result);
            setLoading(false);
          })
          .catch(() => setLoading(false));
      }
    },
    [onSearch],
  );

  useEffect(() => {
    if (!value) {
      setInputValue('');
      setCachedValue('');
      setLoadedOptions([]);
    }
  }, [value]);

  const { getRootProps, getInputProps, groupedOptions, getOptionProps } =
    useAutocomplete({
      id: 'tree-view-autocomplete',
      options: onSearch ? loadedOptions : flatOptions,
      value: (value as unknown as T) || null,
      clearOnBlur: false,
      getOptionLabel,
      blurOnSelect: true,
      isOptionEqualToValue: (option) => option.id === value,
      disabled: isDisabled,
      inputValue,
      onChange: (event, val) => {
        if (val && onLoadTree) {
          setLoading(true);
          onLoadTree(val.id)
            .then((result) => {
              setUnitTree(result);

              const unitsFounded = getFlatTree(result);
              const unitFound = unitsFounded.find((unit) => unit.id === val.id);

              if (unitFound?.disabled) return;
              handleChange(val.id, unitFound ?? val);
              setInputValue(val.label);
              setCachedValue(val.label);
            })
            .catch(() => {
              handleChange(val ? val.id : '', val ?? undefined);
              setInputValue(val ? val.label : '');
              setCachedValue(val ? val.label : '');
            })
            .finally(() => {
              setLoading(false);
            });
        } else {
          handleChange(val ? val.id : '', val ?? undefined);
          setInputValue(val ? val.label : '');
          setCachedValue(val ? val.label : '');
        }
      },
      filterOptions: createFilterOptions<T>({ ignoreAccents: false }),
      onInputChange: (event, nextValue, reason) => {
        if (reason !== 'reset') {
          handleSearch?.(nextValue);
          setInputValue(nextValue);
        }

        if (nextValue !== '') {
          setOpen(true);
        }
      },
    });

  const popperRef = useRef<null | HTMLDivElement>(null);
  const textInputRef = useRef<null | HTMLDivElement>(null);
  const isPopperOpen =
    treeOpen && (!inputValue || inputValue.length >= MIN_INPUT_VALUE_LENGTH);

  const getHighlightedLabel = useCallback(
    (currentValue: string) => {
      const words = currentValue.split(new RegExp(`(${inputValue})`, 'gi'));

      return (
        <span>
          {words.map((part, i) => (
            <Typography
              className={classes.optionText}
              component="span"
              color={
                part.toLowerCase() === inputValue.toLowerCase()
                  ? 'text.light.primary'
                  : 'text.light.tertirary'
              }
              key={i}
            >
              {part}
            </Typography>
          ))}
        </span>
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [inputValue],
  );

  const handleNext = () => {
    if (!onSearch) return;

    const newFilter = {
      ...filter,
      query: inputValue,
      offset: filter.offset + filter.limit,
    };

    setPaginationLoading(true);
    setFilter(newFilter);
    onSearch(newFilter)
      .then((result) => {
        setLoadedOptions([...loadedOptions, ...result]);
        setPaginationLoading(false);
        setHasMore(!!result.length);
      })
      .catch(() => setPaginationLoading(false));
  };

  const onFocus = useCallback(() => {
    setFocused(true);
    setInputValue('');
    setCachedValue(inputValue);
  }, [inputValue]);

  const handlePopperOutsideClick = useCallback(
    (event: MouseEvent | TouchEvent) => {
      const target = event.target as Node;

      const isTargetInsidePopper = popperRef?.current?.contains(target);
      const wasTextFieldClicked =
        textInputRef?.current?.parentNode?.contains(target);

      if (!wasTextFieldClicked && open && !isTargetInsidePopper) setOpen(false);
    },
    [open, popperRef],
  );

  const handlePopperMouseDown = useCallback((event: React.MouseEvent) => {
    // Не даем сработать событию onBlur() у <TextField>, если кликнули на элементы внутри <Popper>
    event.preventDefault();
  }, []);

  const onClick = useCallback(() => setOpen((prev) => !prev), []);

  const onBlurInner = useCallback(
    (event: React.FocusEvent<HTMLInputElement>) => {
      onBlur?.(event);
      const { relatedTarget } = event;
      const containsTarget = popperRef?.current?.contains(
        relatedTarget as Element,
      );
      if (!containsTarget) {
        setOpen(false);
      }
      setFocused(false);

      setInputValue(cachedValue);
    },
    [cachedValue, onBlur],
  );

  const onTreeViewSelect = useCallback(
    (id: string, newLabel?: string, item?: Item) => {
      handleChange(id, item);
      setInputValue(newLabel || '');
      setCachedValue(newLabel || '');
      setTimeout(() => {
        textInputRef.current?.blur();
      }, 0);
    },
    [handleChange, textInputRef],
  );

  const isShowNoOptionText = !isLoading && groupedOptions.length === 0;
  const isShowMenuOptions = !isLoading && groupedOptions.length !== 0;

  return (
    <div {...getRootProps()}>
      <FormControl
        className={classNames(
          classes.treeViewAutocompleteInput,
          withoutMargins && classes.withoutMargins,
          'aqa_tree_view_autocomplete_input',
        )}
      >
        {label && <Label label={label} required={required} tooltip={tooltip} />}
        <TextField
          data-testid={testId}
          {...rest}
          disabled={isDisabled}
          fullWidth
          variant="outlined"
          autoComplete="off"
          error={error}
          helperText={helperText}
          placeholder={placeholder || label}
          onFocus={onFocus}
          onClick={onClick}
          value={loading ? 'Загрузка...' : inputValue}
          onBlur={onBlurInner}
          inputRef={textInputRef}
          slotProps={{
            htmlInput: {
              className: classes.input,
              ...getInputProps(),
            },
            input: {
              ref: setAnchorEl,
              className: `${classes.inputRoot} aqa_select`,
              disabled: isDisabled,
              endAdornment: loading ? (
                <Preloader />
              ) : (
                <InputAdornment className={classes.endAdornment} position="end">
                  {inputValue && (focused || clearable) && (
                    <Box
                      className={classes.clearButton}
                      onClick={handleClearClick}
                    >
                      <Icon
                        name="cancel_circle"
                        color="stroke.secondary"
                        size="20"
                      />
                    </Box>
                  )}
                  <Box className={classes.arrowButton}>
                    <ArrowDropDownIcon
                      className={classNames({
                        [classes.popupIndicatorOpen]: treeOpen,
                      })}
                    />
                  </Box>
                </InputAdornment>
              ),
            },
          }}
        />
      </FormControl>

      <ClickAwayListener onClickAway={handlePopperOutsideClick}>
        <Popper
          role="presentation"
          open={isPopperOpen}
          ref={popperRef}
          anchorEl={anchorEl}
          style={{
            width: anchorEl ? anchorEl.clientWidth : 'auto',
            zIndex: 101,
          }}
          modifiers={[
            {
              name: 'offset',
              options: {
                offset: [0, 5],
              },
            },
          ]}
          onMouseDown={handlePopperMouseDown}
        >
          <Paper elevation={2} className={classes.paper}>
            <Box py="8">
              {inputValue ? (
                <>
                  {isLoading && (
                    <Box display="flex" justifyContent="center" p="16">
                      <CircularProgress size={30} />
                    </Box>
                  )}

                  {isShowMenuOptions && (
                    <ListBox
                      hasMore={hasMore}
                      dataLength={groupedOptions.length}
                      next={handleNext}
                      loading={isPaginationLoading}
                    >
                      {groupedOptions.map((option, index) => {
                        const highlightedLabel = getHighlightedLabel(
                          getOptionLabel(option),
                        );

                        return (
                          <MenuItem
                            className={classes.menuItem}
                            {...getOptionProps({ option, index })}
                            key={option.id}
                          >
                            {renderSearchOption
                              ? renderSearchOption(option, highlightedLabel)
                              : highlightedLabel}
                          </MenuItem>
                        );
                      })}
                    </ListBox>
                  )}

                  {isShowNoOptionText && (
                    <Box className={classes.menuItem}>
                      <Typography
                        color="text.light.tertirary"
                        className={classes.optionText}
                      >
                        {noOptionsText}
                      </Typography>
                    </Box>
                  )}
                </>
              ) : (
                <>
                  {isLoading && (
                    <Box display="flex" justifyContent="center" p="16">
                      <CircularProgress size={30} />
                    </Box>
                  )}
                  {!isLoading && (
                    <TreeView
                      items={unitTree}
                      selected={value || ''}
                      selectOnShrink={false}
                      showRulers={false}
                      showCounters={false}
                      onSelect={onTreeViewSelect}
                      onChildrenLoad={onChildrenLoad}
                      renderOption={renderOption}
                    />
                  )}
                </>
              )}
            </Box>
          </Paper>
        </Popper>
      </ClickAwayListener>
    </div>
  );
};
